// Code snippets that reproduce the Playground's current selection (tool + arguments)
// with the real SDKs (@symone/sdk, symone-sdk) and the workspace MCP endpoint.
import { GATEWAY_PUBLIC_URL, mcpEndpoint } from '../../api/config'

export const SNIPPET_TABS = ['TypeScript', 'Python', 'MCP'] as const
export type SnippetTab = (typeof SNIPPET_TABS)[number]

export interface Snippet {
  label: string
  code: string
}

const PLACEHOLDER_TOOL = '<tool_name>'

function indentAfterFirst(text: string, pad: string): string {
  return text.split('\n').map((l, i) => (i === 0 ? l : pad + l)).join('\n')
}

/** JSON value → Python literal (True/False/None, double-quoted strings). */
export function toPython(v: unknown, indent = 0): string {
  const pad = ' '.repeat(indent)
  const inner = ' '.repeat(indent + 4)
  if (v === null || v === undefined) return 'None'
  if (v === true) return 'True'
  if (v === false) return 'False'
  if (typeof v === 'number') return Number.isFinite(v) ? String(v) : 'None'
  if (typeof v === 'string') return JSON.stringify(v)
  if (Array.isArray(v)) {
    if (v.length === 0) return '[]'
    return `[\n${v.map((x) => inner + toPython(x, indent + 4)).join(',\n')},\n${pad}]`
  }
  const entries = Object.entries(v as Record<string, unknown>)
  if (entries.length === 0) return '{}'
  return `{\n${entries.map(([k, x]) => `${inner}${JSON.stringify(k)}: ${toPython(x, indent + 4)}`).join(',\n')},\n${pad}}`
}

export function typescriptSnippet(tool: string | null, args: Record<string, unknown>): string {
  const name = JSON.stringify(tool ?? PLACEHOLDER_TOOL)
  return `import { Symone } from "@symone/sdk";

const client = new Symone({
  apiKey: process.env.SYMONE_API_KEY,
  baseUrl: "${GATEWAY_PUBLIC_URL}",
});

const result = await client.tools.execute(${name}, ${JSON.stringify(args, null, 2)});
console.log(result.content);`
}

export function pythonSnippet(tool: string | null, args: Record<string, unknown>): string {
  const name = JSON.stringify(tool ?? PLACEHOLDER_TOOL)
  return `import asyncio

from symone_sdk import AsyncSymone


async def main() -> None:
    # api_key defaults to the SYMONE_API_KEY environment variable
    async with AsyncSymone(base_url="${GATEWAY_PUBLIC_URL}") as client:
        result = await client.tools.execute(
            ${name},
            ${indentAfterFirst(toPython(args, 0), '            ')},
        )
        print(result.content)


asyncio.run(main())`
}

export function mcpConfigSnippet(workspaceId: string): string {
  return JSON.stringify(
    {
      mcpServers: {
        symone: {
          type: 'http',
          url: mcpEndpoint(workspaceId),
          headers: { Authorization: 'Bearer <YOUR_SYMONE_API_KEY>' },
        },
      },
    },
    null,
    2,
  )
}

/**
 * SYMONE_SEARCH_TOOLS returns slim results (no input schema unless include_schemas=true);
 * an agent fetches the full schema with SYMONE_GET_TOOL_SCHEMAS before executing.
 */
export function mcpSchemaSnippet(tool: string | null): string {
  return JSON.stringify(
    {
      jsonrpc: '2.0',
      id: 1,
      method: 'tools/call',
      params: { name: 'SYMONE_GET_TOOL_SCHEMAS', arguments: { names: [tool ?? PLACEHOLDER_TOOL] } },
    },
    null,
    2,
  )
}

/** JSON-RPC `tools/call` an MCP client sends: Symone exposes tools via SYMONE_EXECUTE. */
export function mcpCallSnippet(tool: string | null, args: Record<string, unknown>): string {
  return JSON.stringify(
    {
      jsonrpc: '2.0',
      id: 2,
      method: 'tools/call',
      params: {
        name: 'SYMONE_EXECUTE',
        arguments: { tool_name: tool ?? PLACEHOLDER_TOOL, arguments: args },
      },
    },
    null,
    2,
  )
}

export function snippetsFor(
  tab: SnippetTab,
  tool: string | null,
  args: Record<string, unknown>,
  workspaceId: string,
): Snippet[] {
  if (tab === 'TypeScript') return [{ label: 'run-tool.ts', code: typescriptSnippet(tool, args) }]
  if (tab === 'Python') return [{ label: 'run_tool.py', code: pythonSnippet(tool, args) }]
  return [
    { label: 'mcp.json', code: mcpConfigSnippet(workspaceId) },
    { label: '1. tools/call: get the schema', code: mcpSchemaSnippet(tool) },
    { label: '2. tools/call: execute', code: mcpCallSnippet(tool, args) },
  ]
}
